import { useCallback, useEffect, useState } from 'react';
import { api, type OutstandingShareLink } from '../api.ts';

/**
 * The read-only links that are currently outstanding.
 *
 * A share link is the one credential in reef that is not a session. `/shared/:token`
 * sits deliberately outside the auth middleware — the whole point is that
 * sending somebody an incident writeup should not require giving them standing
 * access to the workflow that produced it — which means **the token is the
 * access**, and anyone holding it is inside.
 *
 * Reef could already mint those, from the Artifacts screen. What it could not
 * do was answer "what is outstanding": `GET /share-links` takes a subject, so
 * the only way to revoke one was to already know which artifact it opened,
 * which is precisely what somebody auditing does not know. Links default to a
 * thirty-day expiry rather than none, so this list is short by design — but a
 * list that cannot be seen is a list nobody trims.
 *
 * **No URL is shown, and that is not an omission.** Reef holds a SHA-256 of the
 * token and nothing else, so any link it could render would be a plausible URL
 * that opens nothing. The last four characters tell two links apart, which is
 * all this screen needs them for.
 */
export function ShareLinksCard() {
  const [links, setLinks] = useState<OutstandingShareLink[]>([]);
  const [hidden, setHidden] = useState(0);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const result = await api.outstandingShareLinks();
      setLinks(result.links);
      setHidden(result.hidden);
      setError(null);
    } catch (caught) {
      setError((caught as Error).message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const revoke = async (link: OutstandingShareLink) => {
    setBusy(link.id);
    setError(null);
    try {
      await api.revokeShareLink(link.id, link.workflowId);
      setNote('Revoked. That link stops opening anything immediately.');
      await refresh();
    } catch (caught) {
      setError((caught as Error).message);
    } finally {
      setBusy(null);
    }
  };

  return (
    <section className="studio-card">
      <div className="studio-card-head">
        <h2>Share links</h2>
        {links.length > 0 && <span className="chip">{links.length} outstanding</span>}
        <span className="spacer" />
        <button className="ghost" onClick={() => void refresh()} disabled={loading}>
          Refresh
        </button>
      </div>

      <div className="studio-card-body">
        {error && <div className="banner danger">{error}</div>}
        {note && !error && <div className="banner">{note}</div>}

        <p className="hint" style={{ marginTop: 0 }}>
          A share link opens one run or one artifact without a sign-in, so the token is the access.
          Reef keeps only a digest of it and cannot show you the URL again — the last four
          characters are here to tell two links apart.
        </p>

        {links.length === 0 ? (
          <p className="hint">
            {loading ? 'Looking…' : 'Nothing is outstanding.'}
            {hidden > 0 && ` ${hidden} on workflows you have not been shared on.`}
          </p>
        ) : (
          <>
            <table>
              <thead>
                <tr>
                  <th>Opens</th>
                  <th>Workflow</th>
                  <th>Token</th>
                  <th>Minted by</th>
                  <th>Expires</th>
                  <th>Last used</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {links.map((link) => (
                  <tr key={link.id}>
                    <td>
                      {link.subjectKind} <code>{link.subjectId}</code>
                    </td>
                    <td>{link.workflowName}</td>
                    <td>
                      <code>…{link.tokenHint}</code>
                    </td>
                    <td className="hint">
                      {link.createdBy ?? 'unknown'} ·{' '}
                      {new Date(link.createdAt).toLocaleDateString()}
                    </td>
                    <td>
                      {link.expiresAt === null ? (
                        /*
                         * Flagged rather than shown as a dash. A link with no
                         * expiry is the one that will still be working in a
                         * year, and it is the row worth acting on first.
                         */
                        <span className="chip warn">never</span>
                      ) : link.expired ? (
                        <span className="chip">expired</span>
                      ) : (
                        new Date(link.expiresAt).toLocaleDateString()
                      )}
                    </td>
                    <td className="hint">
                      {/*
                        The difference between a link somebody is relying on and
                        one that was minted and forgotten. Revoking the second
                        costs nothing; revoking the first breaks somebody's day,
                        and this column is how you tell them apart.
                      */}
                      {link.lastUsedAt ? new Date(link.lastUsedAt).toLocaleDateString() : 'never'}
                    </td>
                    <td>
                      {link.canRevoke ? (
                        <button
                          className="ghost"
                          onClick={() => void revoke(link)}
                          disabled={busy !== null}
                        >
                          {busy === link.id ? 'Revoking…' : 'Revoke'}
                        </button>
                      ) : (
                        <span className="hint" title="Revoking needs edit on the workflow">
                          —
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {hidden > 0 && (
              <p className="hint">
                {hidden} more {hidden === 1 ? 'is' : 'are'} outstanding on workflows you have not
                been shared on. A link's existence says a run exists and roughly what it was
                about, so this list is scoped like everything else.
              </p>
            )}
          </>
        )}
      </div>
    </section>
  );
}
